import { Copy } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { toast } from "sonner";
import type { Recipe } from "@/types/recipe";
import { Button } from "@/components/ui/button";

interface RecipeMetadataStripProps {
  recipe: Recipe;
}

export function RecipeMetadataStrip({ recipe }: RecipeMetadataStripProps) {
  const { t } = useLingui();

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr || typeof dateStr !== "string") return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const recipeId = `RCP-${recipe.id}`;

  const handleCopyId = () => {
    navigator.clipboard.writeText(recipeId);
    toast.success(t`Copied to clipboard`);
  };

  return (
    <div className="flex gap-2 items-stretch bg-(--color-surface-muted) p-2 rounded-[6px]">
      <MetaCard label={t`Version Number`} value={`V ${recipe.version}.0`} />

      {/* Recipe ID with copy */}
      <div className="flex-1 flex flex-col gap-2 justify-center p-3 bg-(--color-surface-content) rounded-[6px] min-w-0">
        <p className="text-sm text-(--color-text-secondary) whitespace-nowrap">{t`Recipe ID`}</p>
        <div className="flex items-center gap-2">
          <p className="text-base font-medium text-(--color-text-primary) whitespace-nowrap">{recipeId}</p>
          <Button
            variant="ghost"
            type="button"
            onClick={handleCopyId}
            className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors shrink-0 hover:bg-transparent p-0 h-auto"
            aria-label={t`Copy recipe ID`}
          >
            <Copy className="size-5" />
          </Button>
        </div>
      </div>

      <MetaCard label={t`Created date`} value={formatDate(recipe.createdAt)} />

      {/* Created by with avatar */}
      <div className="flex-1 flex flex-col gap-2 justify-center p-3 bg-(--color-surface-content) rounded-[6px] min-w-0">
        <p className="text-sm text-(--color-text-secondary) whitespace-nowrap">{t`Created by`}</p>
        <div className="flex items-center gap-2 h-[22px]">
          {recipe.createdBy ? (
            <>
              <div className="size-6 rounded-[4px] border border-(--gray-50) bg-(--color-surface-accent) flex items-center justify-center overflow-hidden shrink-0">
                <span className="text-[10px] font-medium text-(--color-text-secondary)">
                  {recipe.createdBy.firstName[0]?.toUpperCase() ?? "?"}
                </span>
              </div>
              <p className="text-base font-medium text-(--color-text-primary) whitespace-nowrap truncate">
                {recipe.createdBy.firstName} {recipe.createdBy.lastName}
              </p>
            </>
          ) : (
            <p className="text-base font-medium text-(--color-text-primary)">—</p>
          )}
        </div>
      </div>

      {/* Last updated — show user initial if available */}
      <div className="flex-1 flex flex-col gap-2 justify-center p-3 bg-(--color-surface-content) rounded-[6px] min-w-0">
        <p className="text-sm text-(--color-text-secondary) whitespace-nowrap">{t`Last updated`}</p>
        <p className="text-base font-medium text-(--color-text-primary) whitespace-nowrap">
          {recipe.updatedBy
            ? `${recipe.updatedBy.firstName} ${recipe.updatedBy.lastName[0]}. • ${formatDate(recipe.updatedAt)}`
            : formatDate(recipe.updatedAt)}
        </p>
      </div>
    </div>
  );
}

function MetaCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex-1 flex flex-col gap-2 justify-center p-3 bg-(--color-surface-content) rounded-[6px] min-w-0">
      <p className="text-sm text-(--color-text-secondary) whitespace-nowrap">{label}</p>
      <p className="text-base font-medium text-(--color-text-primary) whitespace-nowrap">{value}</p>
    </div>
  );
}
